/**
 * API is responsible to all logic related to endpoints.
 * It is responsible to get errors returned from endpoints in different formats and throw a single error message.
 * TODO: throw an object with message and optionally a code or other relevant information.
 */
import axios from "axios";
import {
  getHttpProtocol as scheme,
  getClientServiceUrl as clientServiceUrl,
} from "../utils/requests";
import { refreshAccessTokenInterceptor } from "../utils/refreshTokens";
import {
  requestAccessTokenInterceptor,
  responseStatusInterceptor,
} from "../utils/axios.utils";
import { GymType } from "src/constants/gymSubtypes";

// custom axios

const clientAxios = axios.create({
  baseURL: `${scheme}//${clientServiceUrl}`,
  headers: { "Content-Type": "application/json" },
  validateStatus: null,
});

clientAxios.interceptors.request.use(requestAccessTokenInterceptor);

clientAxios.interceptors.request.use(refreshAccessTokenInterceptor);

clientAxios.interceptors.response.use(responseStatusInterceptor);

// entities types

export interface Staff {
  client_user_id: number;
  user_id: number;
  user_type_id: number;
  client_id: number;
  user_uuid: string;
  uuid: string;
  client_uuid: string;
  first_name: string;
  last_name: string;
}

export interface Tax {
  id: number;
  uuid: string;
  client_id: number;
  is_active: number;
  name: string;
  rate: number;
}

// endpoints

export interface GetStaffResult {
  wasSuccessful: boolean;
  data: {
    page: number;
    limit: number;
    hasMore: boolean;
    total: number;
    data: Staff[];
  };
}

export interface TriggerSnapshotResponse {
  message: string;
}

export async function getStaff(clientUuid: string) {
  const url = `/client/${clientUuid}/staff`;
  const response = await clientAxios.get<GetStaffResult>(url);
  return response.data;
}

export async function triggerSnapshot(client_id: string | number) {
  const url = `/client/${client_id}/snapshot`;

  const response = await clientAxios.post<TriggerSnapshotResponse>(url);

  return response.data;
}

export interface GetTaxesResult {
  wasSuccessful: boolean;
  data: Tax[];
}

export async function getTaxes(clientUuid: string) {
  const url = `/client/taxes/${clientUuid}`;
  const response = await clientAxios.get<GetTaxesResult>(url);
  return response.data;
}

export interface GetFeatureFlagResult {
  clientUuid: string;
  type: string;
  value: boolean;
  name: string;
}

export async function uncached_getFeatureFlag(
  clientUuid: string,
  featureFlag: string,
): Promise<GetFeatureFlagResult> {
  const url = `/client/features/${featureFlag}`;
  const response = await clientAxios.get<GetFeatureFlagResult>(url);
  return response.data;
}

export interface UpdateFeatureFlagPayload {
  name: string;
  value: boolean;
}

export async function updateFeatureFlag(
  clientUuid: string,
  name: string,
  value: boolean,
) {
  const url = `/client/features/update`;
  const response = await clientAxios.post(url, {
    name,
    value,
  });
  return response.data;
}

export interface ClientCreateRequest {
  url: string | null;
  company: string;
  email: string;
  gymType: GymType;
  joinTimestamp: number;
  country: string;
  plan: string;
  trialEnd: number;
  cancelAtPeriod: number;
  firstName: string;
  lastName: string;
  membershipStatus: {
    code: string;
    display: string;
    paused: boolean;
    member_since: string;
  };
  membershipRole: string;
  userSource: string;
  source: string | null;
  name: string;
  couponId: string | null;
  salesTrackingId: string;
  hubspotDealId: string;
  hubspotContactId: string;
  hubspotCompanyId: string;
  isMassEmailEnabled: number;
  additionalScope: string | null;
  triggeredById: string;
  environment: string;
}

export async function createClient(client: ClientCreateRequest) {
  const response = await clientAxios.post("/client/create", client);
  return response.data;
}
